import { createRoute } from '@tanstack/react-router';
import { Route as authedRoute } from '../_authed';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { isAxiosError } from 'axios';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { listLogs, banIp, type LogFilters } from '@/features/admin';

const PAGE = 100;

// Human-readable action labels per event_type (raw type stays as a tooltip).
const EVENT_LABELS: Record<string, { de: string; ru: string }> = {
  'auth.login': { de: 'Anmeldung', ru: 'Вход' },
  'auth.login_failed': { de: 'Anmeldung fehlgeschlagen', ru: 'Неудачный вход' },
  'auth.login_throttled': { de: 'Anmeldung gedrosselt (Limit)', ru: 'Вход заблокирован (лимит)' },
  'auth.logout': { de: 'Abmeldung', ru: 'Выход' },
  'auth.logout_all': { de: 'Überall abgemeldet', ru: 'Выход на всех устройствах' },
  'auth.token_refreshed': { de: 'Sitzung erneuert', ru: 'Сессия обновлена' },
  'auth.session_expired': { de: 'Sitzung abgelaufen', ru: 'Сессия истекла' },
  'access.denied_403': { de: 'Zugriff verweigert', ru: 'Доступ запрещён' },
  'alias.resolved': { de: 'Alias aufgelöst (Suche)', ru: 'Алиас раскрыт (поиск)' },
  'mail.searched': { de: 'Suche', ru: 'Поиск' },
  'mail.folder_viewed': { de: 'Ordner geöffnet', ru: 'Папка открыта' },
  'mail.message_opened': { de: 'E-Mail geöffnet', ru: 'Письмо открыто' },
  'mail.attachment_downloaded': { de: 'Anhang geladen', ru: 'Вложение скачано' },
  'mail.reply_sent': { de: 'Antwort gesendet', ru: 'Ответ отправлен' },
  'mail.sent_as_customer': { de: 'Als Kunde gesendet (Vollmacht)', ru: 'Отправлено от клиента' },
  'mail.message_moved': { de: 'Nachricht verschoben', ru: 'Письмо перемещено' },
  'filter.changed': { de: 'Filter geändert', ru: 'Фильтр изменён' },
  'filter.existing_edited': { de: 'Bestehenden Filter bearbeitet', ru: 'Правило отредактировано' },
  'filter.existing_deleted': { de: 'Filter gelöscht', ru: 'Правило удалено' },
  'user.create': { de: 'Benutzer erstellt', ru: 'Пользователь создан' },
  'user.updated': { de: 'Benutzer geändert', ru: 'Пользователь изменён' },
  'user.password_changed': { de: 'Passwort geändert', ru: 'Пароль изменён' },
  'user.deactivate': { de: 'Benutzer deaktiviert', ru: 'Пользователь деактивирован' },
  'session.revoked_by_admin': { de: 'Sitzungen widerrufen', ru: 'Сессии отозваны' },
  'security.ip_banned': { de: 'IP gesperrt', ru: 'IP забанен' },
  'security.ip_unbanned': { de: 'IP entsperrt', ru: 'IP разбанен' },
  'acl.update': { de: 'Rechte geändert', ru: 'Права изменены' },
  'mailbox.create': { de: 'Postfach erstellt', ru: 'Ящик создан' },
  'mailbox.updated': { de: 'Postfach geändert', ru: 'Ящик изменён' },
  'mailbox.cred_rotated': { de: 'IMAP-Passwort rotiert', ru: 'IMAP-пароль сменён' },
  'mailbox.deactivated': { de: 'Postfach deaktiviert', ru: 'Ящик деактивирован' },
};

const READ_EVENTS = new Set(['mail.searched', 'mail.folder_viewed', 'mail.message_opened', 'mail.attachment_downloaded', 'alias.resolved']);

function eventColor(type: string): 'error' | 'warning' | 'success' | 'primary' | 'default' | 'info' {
  if (type.includes('failed') || type.includes('denied') || type.includes('throttled')) return 'error';
  if (type === 'mail.sent_as_customer' || type === 'mailbox.cred_rotated' || type.startsWith('session') || type.startsWith('security.') || type === 'auth.session_expired') return 'warning';
  if (READ_EVENTS.has(type)) return 'info';  // read access (search/open/download) — distinct from actions
  if (type.startsWith('mail.') || type.startsWith('filter.')) return 'success';
  if (type.startsWith('user.') || type.startsWith('mailbox.') || type.startsWith('acl')) return 'primary';
  return 'default';
}

function LogsPage() {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const lang = i18n.language === 'ru' ? 'ru' : 'de';
  const actionLabel = (ty: string) => EVENT_LABELS[ty]?.[lang] ?? ty;
  const [banMsg, setBanMsg] = useState<string | null>(null);
  const banMut = useMutation({
    mutationFn: (ip: string) => banIp(ip),
    onSuccess: () => setBanMsg(t('mail.f2b.banned')),
    onError: (e) => setBanMsg(isAxiosError(e) ? (e.response?.data?.detail ?? t('mail.f2b.error')) : t('mail.f2b.error')),
  });
  const banFromLog = (ip: string) => { if (window.confirm(t('mail.f2b.confirmBan', { ip }))) banMut.mutate(ip); };

  const filters: LogFilters = {
    event_type: type || undefined,
    user_email: userEmail || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo ? `${dateTo} 23:59:59` : undefined,
    q: q || undefined,
    limit: PAGE,
    offset,
  };
  const logsQ = useQuery({ queryKey: ['logs', filters], queryFn: () => listLogs(filters) });

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(i18n.language);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h2" component="h1" sx={{ mb: 2 }}>{t('mail.logs.title')}</Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="lt">{t('mail.logs.type')}</InputLabel>
          <Select labelId="lt" label={t('mail.logs.type')} value={type} onChange={(e) => { setType(e.target.value); setOffset(0); }}>
            <MenuItem value=""><em>{t('mail.logs.all')}</em></MenuItem>
            {logsQ.data?.types.map((ty) => <MenuItem key={ty} value={ty}>{ty}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label={t('mail.logs.user')} placeholder="vi.syrko@…" value={userEmail} onChange={(e) => { setUserEmail(e.target.value); setOffset(0); }} />
        <TextField size="small" type="date" label={t('mail.logs.from')} InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }} />
        <TextField size="small" type="date" label={t('mail.logs.to')} InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setOffset(0); }} />
        <TextField size="small" sx={{ flexGrow: 1, minWidth: 220 }} label={t('mail.logs.search')} placeholder={t('mail.logs.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} />
      </Stack>

      {logsQ.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {logsQ.isError && (
        <Alert severity={isAxiosError(logsQ.error) && logsQ.error.response?.status === 403 ? 'warning' : 'error'}>
          {isAxiosError(logsQ.error) && logsQ.error.response?.status === 403 ? t('mail.logs.noAccess') : t('mail.logs.error')}
        </Alert>
      )}

      {logsQ.data && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('mail.logs.time')}</TableCell>
                <TableCell>{t('mail.logs.type')}</TableCell>
                <TableCell>{t('mail.logs.user')}</TableCell>
                <TableCell>{t('mail.logs.target')}</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>{t('mail.logs.detail')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsQ.data.items.map((e) => (
                <Fragment key={e.id}>
                  <TableRow hover sx={{ cursor: 'pointer', '& > td': { borderBottom: expanded === e.id ? 'none' : undefined } }}
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(e.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={e.event_type} arrow placement="top">
                        <Chip size="small" label={actionLabel(e.event_type)} color={eventColor(e.event_type)} variant="outlined" />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{e.user_email || '—'}</TableCell>
                    <TableCell><Typography variant="caption">{e.target || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>{e.ip || '—'}</Typography></TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.detail || ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {expanded === e.id && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2"><strong>{t('mail.logs.type')}:</strong> {actionLabel(e.event_type)} <Typography component="span" variant="caption" color="textSecondary">({e.event_type})</Typography></Typography>
                          <Typography variant="body2"><strong>{t('mail.logs.user')}:</strong> {e.user_email || '—'}</Typography>
                          <Typography variant="body2"><strong>{t('mail.logs.target')}:</strong> {e.target || '—'}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2"><strong>IP:</strong> {e.ip || '—'}</Typography>
                            {e.ip && (
                              <Button size="small" color="error" variant="outlined" startIcon={<BlockIcon />}
                                disabled={banMut.isPending}
                                onClick={(ev) => { ev.stopPropagation(); banFromLog(e.ip as string); }}>
                                {t('mail.f2b.ban')}
                              </Button>
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}><strong>{t('mail.logs.detail')}:</strong> {e.detail || '—'}</Typography>
                          <Typography variant="caption" color="textSecondary">{fmt(e.created_at)}</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← {t('mail.logs.prev')}</Button>
            <Typography variant="caption" color="textSecondary">
              {offset + 1}–{Math.min(offset + PAGE, logsQ.data.total)} / {logsQ.data.total}
            </Typography>
            <Button size="small" disabled={offset + PAGE >= logsQ.data.total} onClick={() => setOffset(offset + PAGE)}>{t('mail.logs.next')} →</Button>
          </Stack>
        </>
      )}
      {banMsg && (
        <Alert severity="info" onClose={() => setBanMsg(null)} sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}>{banMsg}</Alert>
      )}
    </Box>
  );
}

export const Route = createRoute({
  getParentRoute: () => authedRoute,
  path: 'logs',
  component: LogsPage,
});
